import { Company } from '../../../../types';
import ApiClient from '../../../../shared/ApiCliente';

export interface AcaoDetalheDto {
  atualizadoEm: string;
  dividendYield: number | null;
  logoUrl: string | null;
  lucroPorAcao: number | null;
  moeda: string | null;
  nome: string;
  precoAtual: number | null;
  precoLucro: number | null;
  resumoEmpresa: string | null;
  setor: string | null;
  ticker: string;
  ultimoDividendo: number | null;
  valorDeMercado: number | null;
  variacao: number | null;
  variacaoPercentual: number | null;
  volume: number | null;
  website: string | null;
  range?: string;
  interval?: string;
  historicalDataPrice?: Array<{
    data: string;
    abertura: number;
    maxima: number;
    minima: number;
    fechamento: number;
    fechamentoAjustado: number;
    volume: number;
  }>;
}

export interface AcaoFundamentosDto {
  results?: Array<{
    symbol?: string;
    shortName?: string;
    longName?: string;
    currency?: string;
    regularMarketPrice?: number | null;
    regularMarketChange?: number | null;
    regularMarketChangePercent?: number | null;
    marketCap?: number | null;
    regularMarketVolume?: number | null;
    priceEarnings?: number | null;
    earningsPerShare?: number | null;
    fiftyTwoWeekRange?: string | null;
    logourl?: string | null;
  }>;
  longName: string | null;
  logourl: string | null;
  marketCap: number | null;
  priceEarnings: number | null;
  summaryProfile?: {
    longBusinessSummary?: string | null;
    website?: string | null;
  } | null;
  defaultKeyStatistics?: {
    profitMargins?: number | null;
    forwardPE?: number | null;
    enterpriseValue?: number | null;
  } | null;
  incomeStatementHistoryQuarterly?: Array<{
    endDate: string;
    totalRevenue: number | null;
    grossProfit: number | null;
    netIncome: number | null;
  }>;
  balanceSheetHistoryQuarterly?: Array<{
    endDate: string;
    cash: number | null;
    totalAssets: number | null;
    totalLiabilities: number | null;
  }>;
  cashflowHistoryQuarterly?: Array<{
    endDate: string;
    operatingCashFlow: number | null;
    freeCashFlow: number | null;
  }>;
}

type HistoricalPoint = {
  data: string | number;
  abertura: number;
  maxima: number;
  minima: number;
  fechamento: number;
  fechamentoAjustado: number;
  volume: number;
};

const parseHistoricalDate = (value: string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsedNumber = new Date(normalized);
    return Number.isNaN(parsedNumber.getTime()) ? null : parsedNumber;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    const asNumber = Number(trimmed);
    const normalized = asNumber < 1_000_000_000_000 ? asNumber * 1000 : asNumber;
    const parsedNumeric = new Date(normalized);
    if (!Number.isNaN(parsedNumeric.getTime())) {
      return parsedNumeric;
    }
  }

  const parsedString = new Date(trimmed);
  return Number.isNaN(parsedString.getTime()) ? null : parsedString;
};

const getLastHistoricalPoint = (history: HistoricalPoint[] | undefined): HistoricalPoint | null => {
  if (!history || history.length === 0) return null;

  const sorted = [...history].sort((a, b) => {
    const aDate = parseHistoricalDate(a.data)?.getTime() || 0;
    const bDate = parseHistoricalDate(b.data)?.getTime() || 0;
    return aDate - bDate;
  });

  return sorted[sorted.length - 1] || null;
};

const isSameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const companyService = {
  getAllCompanies: async (): Promise<Company[]> => {
    try {
      const response = await ApiClient.get<Company[]>('/empresas');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar todas as empresas:', error);
      return [];
    }
  },
  getCompanyByTicker: async (ticker: string, range = '1mo'): Promise<AcaoDetalheDto | null> => {
    try {
      const response = await ApiClient.get<AcaoDetalheDto>(`/acoes/${ticker}`, {
        params: { range },
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar detalhes da empresa ${ticker}:`, error);
      return null;
    }
  },
  getTickerPriceData: async (ticker: string, range = '1mo'): Promise<AcaoDetalheDto | null> => {
    const interval = range === '1d' ? '1h' : '1d';

    try {
      const response = await ApiClient.get<AcaoDetalheDto>(`/acoes/${ticker}`, {
        params: { range, interval },
      });
      const baseData = response.data;

      // Para ranges acima de 1d, usamos diário e fazemos fallback intraday
      // quando o último candle diário está defasado em relação ao dia atual.
      if (range !== '1d') {
        const baseHistory = baseData?.historicalDataPrice || [];
        const baseLastPoint = getLastHistoricalPoint(baseHistory as HistoricalPoint[]);
        const baseLastDate = parseHistoricalDate(baseLastPoint?.data);
        const today = new Date();

        const shouldFetchIntradayFallback = !baseLastDate || !isSameDay(baseLastDate, today);

        if (shouldFetchIntradayFallback) {
          try {
            const intradayResponse = await ApiClient.get<AcaoDetalheDto>(`/acoes/${ticker}`, {
              params: { range: '1d', interval: '1h' },
            });

            const intradayLastPoint = getLastHistoricalPoint(
              (intradayResponse.data?.historicalDataPrice || []) as HistoricalPoint[]
            );
            const intradayLastDate = parseHistoricalDate(intradayLastPoint?.data);

            if (intradayLastPoint && intradayLastDate) {
              const baseLastTimestamp = baseLastDate?.getTime() || 0;
              const intradayLastTimestamp = intradayLastDate.getTime();

              if (intradayLastTimestamp > baseLastTimestamp) {
                return {
                  ...baseData,
                  historicalDataPrice: [...baseHistory, intradayLastPoint as any],
                };
              }
            }
          } catch (intradayError) {
            console.error(`Erro ao buscar fallback intraday para ${ticker}:`, intradayError);
          }
        }
      }

      return baseData;
    } catch (error) {
      console.error(`Erro ao buscar histórico por ticker ${ticker}:`, error);
      return null;
    }
  },
  getFundamentalsByTicker: async (ticker: string): Promise<AcaoFundamentosDto | null> => {
    try {
      const response = await ApiClient.get<AcaoFundamentosDto>(`/acoes/${ticker}/fundamentos`);
      return response.data;
    } catch (error) {
      try {
        const fallbackResponse = await ApiClient.get<AcaoFundamentosDto>('/acoes/fundamentos', {
          params: { ticker },
        });
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error(`Erro ao buscar fundamentos da empresa ${ticker}:`, fallbackError);
        return null;
      }
    }
  },
};


